import { Button } from '@otter-ai/den-components';
import { InformationIcon } from '@otter-ai/den-icons';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { SALES_URL } from '@/constants/external-urls';
import { CRM_PAYWALL_TEST_IDS } from '../test-ids/crm-paywall';
import { trackCRM } from '../tracking/crm-tracking';
import { CrmUpgradeButton } from './CrmUpgradeButton';

type CrmLimitPaywallProps = {
  service: 'salesforce' | 'hubspot';
  messageKey: string;
  connectionsRemaining?: number;
  connectionsUsage: number;
  showUpgradeButton?: boolean;
  showContactSalesButton?: boolean;
  limitReached: boolean;
};

export function CrmLimitPaywall({
  service,
  messageKey,
  connectionsRemaining,
  connectionsUsage,
  showUpgradeButton = false,
  showContactSalesButton = false,
  limitReached,
}: CrmLimitPaywallProps) {
  const t = useTranslations(`settings:integrations.apps.${service}`);

  const contactSalesUrl = `${SALES_URL}?utm_content=${encodeURIComponent('SS Bus Admins')}`;

  return (
    <div
      className="w-full flex flex-row gap-2 justify-between items-center border rounded-lg border-subtle px-4 py-3 mt-4"
      data-testid={CRM_PAYWALL_TEST_IDS.paywall(service)}
    >
      <div className="flex flex-row gap-2 items-center">
        <InformationIcon className="w-4 h-4" />
        <p data-testid={CRM_PAYWALL_TEST_IDS.message(service)}>
          {t.rich(`crmLimitReached.${messageKey}`, {
            bold: (chunks) => <span className="font-semibold">{chunks}</span>,
            remaining: connectionsRemaining ?? 0,
          })}
        </p>
      </div>
      {showUpgradeButton && (
        <CrmUpgradeButton
          service={service}
          connectionsUsage={connectionsUsage}
          limitReached={limitReached}
        />
      )}
      {showContactSalesButton && (
        <Button
          asChild
          data-testid={CRM_PAYWALL_TEST_IDS.contactSalesButton(service)}
        >
          <Link
            href={contactSalesUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCRM.paywall.clickContactSales(service)}
          >
            {t('contactSalesButton')}
          </Link>
        </Button>
      )}
    </div>
  );
}
